import { useTranslations } from '@vocab/react';
import {
  Badge,
  Drawer,
  Stack,
  Tab,
  TabPanel,
  TabPanels,
  Tabs,
  TabsProvider,
} from 'braid-design-system';

import { useAnalytics } from 'src/modules/analytics/AnalyticsProvider';

import translations from './.vocab';
import { AvailableVerifications } from './AvailableVerifications/AvailableVerifications';
import { YourVerifications } from './YourVerifications/YourVerifications';
import {
  verificationsDrawerCurrentTabToAnalyticsProps,
  verificationsDrawerTargetTabToEventName,
} from './analyticsUtils';
import { useVerifications } from './data';

const VERIFICATIONS_DRAWER_TABS = ['verified', 'verifiable'];
export type VerificationsDrawerTab = (typeof VERIFICATIONS_DRAWER_TABS)[number];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  activeTab: string;
  setActiveTabFn: (tab: string) => void;
}

export const VerificationsDrawer = ({
  isOpen,
  onClose,
  activeTab,
  setActiveTabFn,
}: Props) => {
  const { t } = useTranslations(translations);

  const { verificationsTabClicked } = useAnalytics();

  const { verifiableCredentialCount } = useVerifications();

  const verifiableCountBadge =
    verifiableCredentialCount && verifiableCredentialCount > 0 ? (
      <Badge tone="neutral">{verifiableCredentialCount}</Badge>
    ) : undefined;

  const onTabChange = (tab: VerificationsDrawerTab | undefined) => {
    if (tab) {
      const eventName = verificationsDrawerTargetTabToEventName[tab];
      const { currentPageSubsection } =
        verificationsDrawerCurrentTabToAnalyticsProps[activeTab];

      verificationsTabClicked({ eventName, currentPageSubsection });

      setActiveTabFn(tab);
    }
  };

  return (
    <Drawer
      id="verifications-drawer"
      data={{ automation: 'verifications-drawer' }}
      title={t('Verifications')}
      width="small"
      open={isOpen}
      onClose={onClose}
      closeLabel={t('Close')}
    >
      <TabsProvider
        id="verifications-tabs"
        selectedItem={activeTab}
        onChange={(_, tab) => onTabChange(tab)}
      >
        <Stack space="large">
          <Tabs label="Verifications">
            <Tab
              item="verified"
              data={{
                automation: 'verifications-verified-tab-button',
              }}
            >
              {t('Your verifications')}
            </Tab>
            <Tab
              item="verifiable"
              badge={verifiableCountBadge}
              data={{
                automation: 'verifications-available-tab-button',
              }}
            >
              {t('Available verifications')}
            </Tab>
          </Tabs>
          <TabPanels>
            <TabPanel item="verified">
              <YourVerifications />
            </TabPanel>
            <TabPanel item="verifiable">
              <AvailableVerifications />
            </TabPanel>
          </TabPanels>
        </Stack>
      </TabsProvider>
    </Drawer>
  );
};
